import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import api from '../../../lib/api';

const getRiskColor = (risk) => {
  if (risk >= 80) return { color: 'var(--color-error)', bg: '#FEE2E2', label: 'Critical' };
  if (risk >= 65) return { color: 'var(--color-warning)', bg: '#FEF3C7', label: 'High' };
  return { color: 'var(--color-success)', bg: '#D1FAE5', label: 'Medium' };
};

const ChurnPrediction = () => {
  const [churnAlerts, setChurnAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChurnRisk = async () => {
      try {
        setLoading(true);

        const { data: allMembers } = await api.get('/members', { params: { limit: 200 } });

        const in30Days = new Date();
        in30Days?.setDate(in30Days?.getDate() + 30);
        const todayStr = new Date()?.toISOString()?.split('T')?.[0];
        const in30DaysStr = in30Days?.toISOString()?.split('T')?.[0];

        // Members with packages expiring in next 30 days
        const expiringMembers = (allMembers || [])
          ?.filter(m => m?.package_end_date && m?.package_end_date >= todayStr && m?.package_end_date <= in30DaysStr)
          ?.slice(0, 10);

        if (expiringMembers?.length > 0) {
          const alerts = expiringMembers?.map(m => {
            const endDate = new Date(m?.package_end_date);
            const daysLeft = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
            const riskScore = Math.max(100 - daysLeft * 3, 40);
            return {
              id: m?.id,
              name: `${m?.first_name || ''} ${m?.last_name || ''}`?.trim() || '—',
              risk: riskScore,
              lastVisit: `${daysLeft} days left`,
              reason: daysLeft <= 7 ? 'Package expiring very soon' : daysLeft <= 14 ? 'Package expiring soon' : 'Package expiring in 30 days',
              plan: m?.package_name || 'Unknown',
              value: '$0',
            };
          });
          setChurnAlerts(alerts);
        } else {
          // Fallback: show inactive members as churn risk
          const inactiveMembers = (allMembers || [])?.filter(m => m?.member_status === 'inactive')?.slice(0, 4);
          const alerts = inactiveMembers?.map((m, i) => ({
            id: m?.id,
            name: `${m?.first_name || ''} ${m?.last_name || ''}`?.trim() || '—',
            risk: 85 - i * 8,
            lastVisit: 'Recently inactive',
            reason: 'Membership inactive',
            plan: m?.package_name || 'Unknown',
            value: '$0',
          }));
          setChurnAlerts(alerts);
        }
      } catch (err) {
        console.error('Error fetching churn risk:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChurnRisk();
  }, []);

  if (loading) {
    return (
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-2 text-[var(--color-muted-foreground)]">
          <Icon name="Loader2" size={16} color="currentColor" className="animate-spin" />
          <span className="text-sm font-caption">Loading churn predictions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-4 lg:p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-base lg:text-lg font-semibold font-heading text-[var(--color-foreground)]">
            Churn Prediction Alerts
          </h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-caption mt-0.5">
            Members with expiring packages and high churn risk
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 rounded-full">
          <Icon name="AlertTriangle" size={13} color="var(--color-error)" />
          <span className="text-xs font-medium font-caption text-red-700">{churnAlerts?.length} at risk</span>
        </div>
      </div>
      {churnAlerts?.length === 0 ? (
        <div className="text-center py-6 text-sm text-[var(--color-muted-foreground)] font-caption">
          No churn risks detected. All packages are healthy.
        </div>
      ) : (
        <div className="space-y-3">
          {churnAlerts?.map((member) => {
            const risk = getRiskColor(member?.risk);
            return (
              <div
                key={member?.id}
                className="p-3 lg:p-4 rounded-xl border border-[var(--color-border)] hover:border-red-200 transition-colors"
                style={{ backgroundColor: `${risk?.color}06` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold font-heading text-[var(--color-foreground)]">
                        {member?.name}
                      </p>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-caption font-medium"
                        style={{ backgroundColor: risk?.bg, color: risk?.color }}
                      >
                        {risk?.label}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-caption mt-1">
                      {member?.reason}
                    </p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-xs text-[var(--color-muted-foreground)] font-caption flex items-center gap-1">
                        <Icon name="Clock" size={11} color="var(--color-muted-foreground)" />
                        {member?.lastVisit}
                      </span>
                      <span className="text-xs text-[var(--color-muted-foreground)] font-caption">
                        {member?.plan} · {member?.value}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-xl font-bold font-heading" style={{ color: risk?.color }}>
                        {member?.risk}%
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)] font-caption">churn risk</p>
                    </div>
                    <Button variant="outline" size="xs" iconName="MessageCircle" iconPosition="left" iconSize={11}>
                      Reach Out
                    </Button>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[var(--color-muted-foreground)] font-caption">Risk Score</span>
                    <span className="text-xs font-medium font-caption" style={{ color: risk?.color }}>
                      {member?.risk}/100
                    </span>
                  </div>
                  <div className="w-full bg-[var(--color-muted)] rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${member?.risk}%`, backgroundColor: risk?.color }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChurnPrediction;